import React, { useEffect, useRef } from 'react';
import cn from '../../lib/cn';
import useInView from '../../hooks/useInView';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';

/**
 * A silent, looping background clip that only plays while it is on screen —
 * so a page full of video does not decode eight streams at once. Visitors who
 * prefer reduced motion get the poster frame and nothing else.
 */
export function AmbientVideo({ src, poster, className, videoClassName, alt = '' }) {
  const [wrapRef, inView] = useInView({ once: false, amount: 0.25, margin: '0px' });
  const videoRef = useRef(null);
  const reduceMotion = usePrefersReducedMotion();

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;

    if (inView) {
      const attempt = video.play();
      // Autoplay can still be refused; the poster stays up, which is fine.
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    } else {
      video.pause();
    }
  }, [inView, reduceMotion]);

  return (
    <div ref={wrapRef} className={cn('relative overflow-hidden', className)}>
      {reduceMotion ? (
        <img src={poster} alt={alt} className={cn('h-full w-full object-cover', videoClassName)} />
      ) : (
        <video
          ref={videoRef}
          className={cn('h-full w-full object-cover', videoClassName)}
          poster={poster}
          muted
          loop
          playsInline
          preload="none"
          aria-label={alt || undefined}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  );
}

export default AmbientVideo;
